/**
 * Maps backend classification levels (0–8) to locale keys and badge styles.
 * Aligns with Algorithm/jupasClassifyCore.js CLASSIFICATION_MAPPING.
 * Badge classes include dark: variants for readable contrast in dark mode.
 */

import React from 'react';
import { getSubjectShortLabel } from './dseSubjects';
import { isInternalTeam } from './internalAccounts';

/** EdVise internal / staff accounts may see technical error codes in the calculation area. */
export function canViewJupasErrorCodes(user) {
  return isInternalTeam(user);
}

/** Shared neutral styling for unavailable classifier / requirement-check results. */
export const JUPAS_UNAVAILABLE_BG_CLASS = 'bg-gray-400 text-white dark:bg-gray-500 dark:text-white';

export function isClassificationUnavailable(classification) {
  return classification === -1;
}

export function isRequirementUnavailable(fulfillment) {
  const value = typeof fulfillment === 'string'
    ? fulfillment
    : fulfillment?.code || fulfillment?.classification;
  return String(value || '').toUpperCase() === 'U';
}

const REQUIREMENT_CODE_TO_CLASSIFICATION = {
  P: 'PASS',
  C: 'CAUTION',
  R: 'RANKING',
  W: 'WARNING',
  U: 'U',
};

/** Badge value for list/detail rows from compact summary or expanded detail data. */
export function resolveRequirementBadgeValue(classificationEntry) {
  if (!classificationEntry) return null;
  if (classificationEntry.requirement_fulfillment != null) {
    return classificationEntry.requirement_fulfillment;
  }

  const requirementCode = String(classificationEntry.requirement || '').toUpperCase();
  if (REQUIREMENT_CODE_TO_CLASSIFICATION[requirementCode]) {
    return {
      classification: REQUIREMENT_CODE_TO_CLASSIFICATION[requirementCode],
      code: requirementCode,
    };
  }

  const requResult = Number(classificationEntry.requirement_check?.requ_result);
  if (!Number.isFinite(requResult)) return null;

  return {
    classification: requResult === 3 ? 'PASS' : 'WARNING',
  };
}

/** True while a detailed requirement result is still expected (show spinner). */
export function isRequirementFulfillmentAwaiting(
  classificationEntry,
  { classificationLoading = false } = {}
) {
  if (classificationLoading) return true;
  if (!classificationEntry) return false;
  if (classificationEntry.requirement != null) return false;
  if (classificationEntry.requirement_fulfillment != null) return false;
  if (resolveRequirementBadgeValue(classificationEntry) != null) return false;
  if (isClassificationUnavailable(classificationEntry.classification)) return false;
  return true;
}

/** Shared High Risk red — also used for W warning icons and − action buttons. */
export const JUPAS_HIGH_RISK_BG_CLASS = 'bg-[#FF4444] text-red-950 dark:bg-[#FF4444] dark:text-red-950';
export const JUPAS_HIGH_RISK_TEXT_CLASS = 'text-[#FF4444]';
export const JUPAS_HIGH_RISK_ACTION_CLASS =
  'text-[#FF4444] hover:text-red-600 active:text-red-700';
/** Shared Very Safe green — also used for P badges and + actions. */
export const JUPAS_VERY_SAFE_BG_CLASS = 'bg-green-400 text-green-950 dark:bg-green-400 dark:text-green-950';
export const JUPAS_VERY_SAFE_ACTION_CLASS =
  'text-green-400 hover:text-green-600 active:text-green-700';

const CLASSIFICATION_BADGE_KEYS = {
  8: 'goldenTicket',
  7: 'secure',
  6: 'verySafe',
  5: 'safe',
  4: 'moderate',
  3: 'risky',
  2: 'veryRisky',
  1: 'dangerous',
  0: 'missionImpossible',
};

const CLASSIFICATION_BADGE_CLASS_NAMES = {
  8: 'bg-green-700 text-white',
  7: JUPAS_VERY_SAFE_BG_CLASS,
  6: 'bg-lime-300 text-lime-950 dark:bg-lime-300 dark:text-lime-950',
  5: 'bg-yellow-300 text-yellow-950 dark:bg-yellow-300 dark:text-yellow-950',
  4: 'bg-orange-300 text-orange-950 dark:bg-orange-300 dark:text-orange-950',
  3: 'bg-red-300 text-red-950 dark:bg-red-300 dark:text-red-950',
  2: JUPAS_HIGH_RISK_BG_CLASS,
  1: 'bg-red-700 text-white',
  0: 'bg-red-900 text-white',
};

const CLASSIFICATION_BADGE_BORDER_CLASS_NAMES = {
  8: 'bg-green-700 text-white border-green-800',
  7: `${JUPAS_VERY_SAFE_BG_CLASS} border-green-700`,
  6: 'bg-lime-300 text-lime-950 border-lime-400 dark:bg-lime-300 dark:text-lime-950 dark:border-lime-400',
  5: 'bg-yellow-300 text-yellow-950 border-yellow-400 dark:bg-yellow-300 dark:text-yellow-950 dark:border-yellow-400',
  4: 'bg-orange-300 text-orange-950 border-orange-400 dark:bg-orange-300 dark:text-orange-950 dark:border-orange-400',
  3: 'bg-red-300 text-red-950 border-red-400 dark:bg-red-300 dark:text-red-950 dark:border-red-400',
  2: `${JUPAS_HIGH_RISK_BG_CLASS} border-red-600`,
  1: 'bg-red-700 text-white border-red-800',
  0: 'bg-red-900 text-white border-red-950',
};

const REQUIREMENT_FULFILLMENT_DISPLAYS = {
  PASS: {
    code: 'P',
    labelKey: 'pass',
    englishLabel: 'PASS',
    className: JUPAS_VERY_SAFE_BG_CLASS,
  },
  CAUTION: {
    code: 'C',
    labelKey: 'caution',
    englishLabel: 'CAUTION',
    className: 'bg-yellow-300 text-yellow-950 dark:bg-yellow-300 dark:text-yellow-950',
  },
  RANKING: {
    code: 'R',
    labelKey: 'rankingIssue',
    englishLabel: 'RANKING',
    className: 'bg-orange-300 text-orange-950 dark:bg-orange-300 dark:text-orange-950',
  },
  WARNING: {
    code: 'W',
    labelKey: 'warning',
    englishLabel: 'WARNING',
    className: JUPAS_HIGH_RISK_BG_CLASS,
  },
  U: {
    code: 'U',
    labelKey: 'unavailable',
    englishLabel: 'UNAVAIL',
    className: JUPAS_UNAVAILABLE_BG_CLASS,
  },
};

/** Compact on mobile; expands to fit "Golden Ticket" at the desktop text size. */
export const CLASSIFICATION_BADGE_WIDTH_CLASS = 'w-12 sm:w-[6.75rem]';

/** Shared responsive type scale for classification labels. */
export const CLASSIFICATION_BADGE_TEXT_CLASS = 'text-[9px] leading-none sm:text-sm sm:leading-normal';

/** Shared responsive size for the round requirement-check label. */
export const REQUIREMENT_BADGE_SIZE_CLASS =
  'h-[18px] w-[18px] text-[9px] sm:h-6 sm:w-6 sm:text-xs';

/** Shared rectangular label matching list-row classification badges. */
export function JupasPanelStatusLabel({ display, className = '' }) {
  if (!display) return null;

  return (
    <span
      className={`inline-flex ${CLASSIFICATION_BADGE_WIDTH_CLASS} ${CLASSIFICATION_BADGE_TEXT_CLASS} shrink-0 items-center justify-center gap-1 rounded px-1 py-1.5 font-medium sm:px-1.5 ${display.className} ${className}`}
      role={display.isLoading ? 'status' : undefined}
      title={display.label}
      aria-label={display.label}
    >
      <span className="truncate">{display.label}</span>
    </span>
  );
}

/** Round requirement checker badge (P / W / C …) used in detail panel and list rows. */
export function JupasRequirementCheckerBadge({ display, className = '' }) {
  if (!display) return null;

  return (
    <span
      className={`inline-flex ${REQUIREMENT_BADGE_SIZE_CLASS} shrink-0 items-center justify-center rounded-full font-bold leading-none ${display.className} ${className}`}
      title={display.label}
      aria-label={display.label}
      role={display.isLoading ? 'status' : undefined}
    >
      {display.isLoading ? (
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" aria-hidden />
      ) : (
        display.code
      )}
    </span>
  );
}

const CLASSIFICATION_UNAVAILABLE_BADGE_CLASS =
  'bg-gray-500 text-white dark:bg-gray-400 dark:text-gray-950';

export function getClassificationBadgeKey(classification) {
  if (isClassificationUnavailable(classification)) {
    return 'unavailable';
  }
  const level = Number(classification);
  if (!Number.isFinite(level)) return null;
  return CLASSIFICATION_BADGE_KEYS[level] ?? null;
}

export function getClassificationLoadingBadgeDisplay(t) {
  return {
    label: t('common.loading'),
    className: 'bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-300',
    isLoading: true,
  };
}

export function getClassificationUnavailableBadgeDisplay(t) {
  return {
    label: t('jupa.classificationBadges.unavailable'),
    className: CLASSIFICATION_UNAVAILABLE_BADGE_CLASS,
    isUnavailable: true,
  };
}

/** @deprecated use getClassificationUnavailableBadgeDisplay */
export function getClassificationErrorBadgeDisplay(t) {
  return getClassificationUnavailableBadgeDisplay(t);
}

export function getClassificationBadgeDisplay(classification, t) {
  if (isClassificationUnavailable(classification)) {
    return getClassificationUnavailableBadgeDisplay(t);
  }

  const level = Number(classification);
  const key = getClassificationBadgeKey(level);
  if (!key || key === 'unavailable') return getClassificationUnavailableBadgeDisplay(t);

  return {
    label: t(`jupa.classificationBadges.${key}`),
    className: CLASSIFICATION_BADGE_CLASS_NAMES[level] ?? 'bg-surface-muted text-ink-muted',
  };
}

export function getClassificationBadgeBorderClassName(classification) {
  const level = Number(classification);
  if (!Number.isFinite(level) || !(level in CLASSIFICATION_BADGE_KEYS)) {
    return 'bg-surface-muted text-ink-muted border-edge';
  }
  return CLASSIFICATION_BADGE_BORDER_CLASS_NAMES[level];
}

export function getClassificationBadgeText(classification, t) {
  const display = getClassificationBadgeDisplay(classification, t);
  return display?.label ?? t('jupa.classificationBadges.unavailable');
}

export function getRequirementFulfillmentLoadingDisplay(t) {
  return {
    code: null,
    label: t('common.loading'),
    className: 'bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-300',
    isLoading: true,
  };
}

export function getRequirementFulfillmentDisplay(fulfillment, t) {
  const rawType = typeof fulfillment === 'string'
    ? fulfillment
    : fulfillment?.classification || fulfillment?.code;
  const type = String(rawType || '').toUpperCase();
  const display = REQUIREMENT_FULFILLMENT_DISPLAYS[type];
  if (!display) return null;

  return {
    ...display,
    englishLabel: display.englishLabel ?? type,
    label: t(`jupa.requirementFulfillment.${display.labelKey}`),
  };
}

/**
 * Returns fulfillment badge data, or a grey spinner while loading / awaiting a result.
 * Pass `awaiting` when a result is expected but not yet available (never leave the slot blank).
 */
export function resolveRequirementFulfillmentDisplay(
  fulfillment,
  t,
  { loading = false, awaiting = false } = {}
) {
  if (loading) return getRequirementFulfillmentLoadingDisplay(t);
  const result = getRequirementFulfillmentDisplay(fulfillment, t);
  if (result) return result;
  if (awaiting) return getRequirementFulfillmentLoadingDisplay(t);
  return null;
}

const CLASSIFICATION_ERROR_CATEGORY_KEYS = {
  INVALID_INPUT: 'invalidInput',
  INCOMPLETE_SCORE_PROFILE: 'incompleteScoreProfile',
  CONFIGURATION_ERROR: 'configurationError',
  MISSING_DATA: 'missingData',
  SUSPICIOUS_DATA: 'suspiciousData',
  SERVICE_ERROR: 'serviceError',
  CLASSIFICATION_ERROR: 'classificationError',
};

function formatClassificationDetailValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function resolveClassificationErrorSummary(error, t) {
  const code = error?.code;
  const details = error?.details && typeof error.details === 'object' ? error.details : {};
  const codeKey = code ? `jupa.classificationErrors.codes.${code}` : null;
  const localizedCodeMessage = codeKey && t(codeKey) !== codeKey
    ? t(codeKey, {
      subject: getSubjectShortLabel(t, details.subject) || details.subject || '—',
      grade: formatClassificationDetailValue(details.grade),
      field: details.field || '—',
      programme_code: details.programme_code || '—',
      code: details.programme_code || '—',
    })
    : null;

  return localizedCodeMessage
    || error?.message
    || t('jupa.classificationUnavailable.message');
}

function buildClassificationErrorDetailLines(error, t, { showErrorCode = false } = {}) {
  if (!error) return [];

  const details = error.details && typeof error.details === 'object' ? error.details : {};
  const lines = [];
  const seen = new Set();
  const push = (line) => {
    const text = String(line || '').trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    lines.push(text);
  };

  if (details.programme_code) {
    push(t('jupa.classificationErrors.details.programmeCode', { code: details.programme_code }));
  }
  if (details.subject) {
    const subject = getSubjectShortLabel(t, details.subject) || details.subject;
    push(t('jupa.classificationErrors.details.subject', { subject }));
  }
  if (details.grade != null && details.grade !== '') {
    push(t('jupa.classificationErrors.details.unexpectedGrade', {
      grade: formatClassificationDetailValue(details.grade),
    }));
  }
  if (details.field) {
    push(t('jupa.classificationErrors.details.field', { field: details.field }));
  }
  if (details.weight != null && details.weight !== '') {
    push(t('jupa.classificationErrors.details.unexpectedWeight', {
      weight: formatClassificationDetailValue(details.weight),
    }));
  }
  if (details.httpStatus != null || details.status != null) {
    push(t('jupa.classificationErrors.details.httpStatus', {
      status: formatClassificationDetailValue(details.httpStatus ?? details.status),
    }));
  }
  if (
    details.score_uq !== undefined
    || details.score_md !== undefined
    || details.score_lq !== undefined
  ) {
    push(t('jupa.classificationErrors.details.partialCutoffs', {
      uq: formatClassificationDetailValue(details.score_uq),
      md: formatClassificationDetailValue(details.score_md),
      lq: formatClassificationDetailValue(details.score_lq),
    }));
  }
  if (details.requ_result !== undefined || details.calc_result !== undefined) {
    push(t('jupa.classificationErrors.details.calculationInputs', {
      requ_result: formatClassificationDetailValue(details.requ_result),
      calc_result: formatClassificationDetailValue(details.calc_result),
    }));
  }

  const summary = resolveClassificationErrorSummary(error, t);
  if (error.message && error.message !== summary) {
    push(t('jupa.classificationErrors.details.serverMessage', { message: error.message }));
  }
  if (showErrorCode && error.code) {
    push(t('jupa.classificationErrors.details.errorCode', { code: error.code }));
  }

  return lines;
}

/**
 * @returns {{ categoryLabel: string|null, summary: string, detailLines: string[] }}
 */
export function getClassificationUnavailableExplanation({ error, classification } = {}, t, options = {}) {
  const { showErrorCode = false } = options;
  const unavailable = isClassificationUnavailable(classification);
  if (!error && !unavailable) {
    return {
      categoryLabel: null,
      summary: t('jupa.classificationErrors.fallback'),
      detailLines: [],
    };
  }

  const categoryKey = CLASSIFICATION_ERROR_CATEGORY_KEYS[String(error?.category || '').toUpperCase()];
  const categoryLabel = categoryKey
    ? t(`jupa.classificationErrors.categories.${categoryKey}`)
    : error?.category
      ? t('jupa.classificationErrors.categories.classificationError')
      : null;

  const summary = resolveClassificationErrorSummary(error, t);

  return {
    categoryLabel,
    summary,
    detailLines: buildClassificationErrorDetailLines(error, t, { showErrorCode }),
  };
}

export function ClassificationUnavailableExplanation({
  error,
  classification,
  t,
  className = 'text-sm leading-relaxed text-ink-muted',
  categoryClassName = 'text-sm font-medium text-ink',
  showErrorCode = false,
}) {
  const { categoryLabel, summary, detailLines } = getClassificationUnavailableExplanation(
    { error, classification },
    t,
    { showErrorCode }
  );

  return (
    <div className={className}>
      {categoryLabel ? (
        <p className={`${categoryClassName} mb-1`}>{categoryLabel}</p>
      ) : null}
      <p>{summary}</p>
      {detailLines.length ? (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-ink-subtle">
          {detailLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function getClassificationNoticeMessage(notice, t) {
  if (!notice) return null;
  const code = notice.code ? String(notice.code) : '';
  if (code) {
    const key = `jupa.classificationNotices.${code}`;
    const localized = t(key);
    if (localized !== key) return localized;
  }
  return notice.message || null;
}
